/**
 * Frontend Entry Point
 *
 * This file is the entry point for the React app, included by
 * src/index.html. It bootstraps runtime config, telemetry, and
 * then mounts the App component.
 *
 * Runtime config must be loaded before App is imported because App.tsx
 * calls getMsalConfig() at module scope to create the MSAL instance.
 */

import React from "react";
import { createRoot } from "react-dom/client";
import { initRuntimeConfig } from "./config/runtime";
import { initTelemetry } from "./config/telemetry";
import "./index.css";

async function bootstrap() {
  // Phase 1: Load runtime config (fetches /config from the server)
  await initRuntimeConfig();

  // Phase 2: Initialize Application Insights (noop if connection string is empty)
  initTelemetry();

  // Phase 3: Dynamically import App so module-scope code in App.tsx
  // (e.g., new PublicClientApplication(getMsalConfig())) runs after
  // runtime config is available.
  const { App } = await import("./App");

  const elem = document.getElementById("root")!;
  const app = (
    <React.StrictMode>
      <App />
    </React.StrictMode>
  );

  // Preserve the React root across HMR updates to avoid remounting the
  // entire tree (which would lose component state). In production builds
  // import.meta.hot is undefined, so this falls back to a fresh root.
  // https://bun.sh/docs/bundler/hot-reloading#import-meta-hot-data
  if (import.meta.hot) {
    if (!import.meta.hot.data.root) {
      import.meta.hot.data.root = createRoot(elem);
    }
    import.meta.hot.data.root.render(app);
  } else {
    createRoot(elem).render(app);
  }
}

bootstrap().catch((error) => {
  console.error("Failed to bootstrap application:", error);
  // Show a minimal error UI if bootstrap fails entirely
  const root = document.getElementById("root");
  if (root) {
    root.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:sans-serif;background:#0f172a;color:#e2e8f0">
        <div style="text-align:center;max-width:400px;padding:2rem">
          <h1 style="font-size:1.5rem;margin-bottom:1rem">Failed to load application</h1>
          <p style="color:#94a3b8;margin-bottom:1.5rem">Please try refreshing the page. If the problem persists, contact support.</p>
          <button onclick="location.reload()" style="padding:0.5rem 1.5rem;background:#ff5500;color:white;border:none;border-radius:0.5rem;cursor:pointer;font-size:0.875rem">
            Reload Page
          </button>
        </div>
      </div>
    `;
  }
});
